// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
// Mirrors packages/sequent-core/src/types/messaging.rs.
export var MESSAGING_CONFIG_ANNOTATION = "messaging:config";
export var EVENT_MESSAGING_CONFIG_VERSION = 1;
export var EMessageChannel;
(function (EMessageChannel) {
    EMessageChannel["EMAIL"] = "EMAIL";
    EMessageChannel["SMS"] = "SMS";
    EMessageChannel["WHATSAPP"] = "WHATSAPP";
    EMessageChannel["VIBER"] = "VIBER";
    EMessageChannel["MESSENGER"] = "MESSENGER";
})(EMessageChannel || (EMessageChannel = {}));
/** Channel order everywhere: WhatsApp, Viber, Messenger, SMS, Email. */
export var MESSAGE_CHANNELS = [
    EMessageChannel.WHATSAPP,
    EMessageChannel.VIBER,
    EMessageChannel.MESSENGER,
    EMessageChannel.SMS,
    EMessageChannel.EMAIL,
];
export var EMessagePurpose;
(function (EMessagePurpose) {
    EMessagePurpose["OTP"] = "OTP";
    EMessagePurpose["NOTICE"] = "NOTICE";
})(EMessagePurpose || (EMessagePurpose = {}));
export var MESSAGE_PURPOSES = [EMessagePurpose.OTP, EMessagePurpose.NOTICE];
export var ERecipientKind;
(function (ERecipientKind) {
    ERecipientKind["EMAIL_ADDRESS"] = "EMAIL_ADDRESS";
    ERecipientKind["PHONE_NUMBER"] = "PHONE_NUMBER";
    ERecipientKind["PAGE_SCOPED_ID"] = "PAGE_SCOPED_ID";
})(ERecipientKind || (ERecipientKind = {}));
export var EMessagingProvider;
(function (EMessagingProvider) {
    EMessagingProvider["AWS_SES"] = "AWS_SES";
    EMessagingProvider["SMTP"] = "SMTP";
    EMessagingProvider["AWS_SNS"] = "AWS_SNS";
    EMessagingProvider["WHATSAPP_CLOUD_API"] = "WHATSAPP_CLOUD_API";
    EMessagingProvider["MESSENGER_SEND_API"] = "MESSENGER_SEND_API";
    EMessagingProvider["VIBER_INFOBIP"] = "VIBER_INFOBIP";
    /** Parse-only: an account saved with it loads as unsupported and can't send. */
    EMessagingProvider["HTTP_API"] = "HTTP_API";
    /** Parse-only: an account saved with it loads as unsupported and can't send. */
    EMessagingProvider["CONSOLE"] = "CONSOLE";
})(EMessagingProvider || (EMessagingProvider = {}));
/** Providers that were removed: their accounts still load, to be replaced. */
export var UNSUPPORTED_PROVIDERS = [
    EMessagingProvider.HTTP_API,
    EMessagingProvider.CONSOLE,
];
export var EDeliveryFeedback;
(function (EDeliveryFeedback) {
    EDeliveryFeedback["PROVIDER_RECEIPTS"] = "PROVIDER_RECEIPTS";
    EDeliveryFeedback["UNAVAILABLE"] = "UNAVAILABLE";
})(EDeliveryFeedback || (EDeliveryFeedback = {}));
export var EMessageAttemptState;
(function (EMessageAttemptState) {
    EMessageAttemptState["QUEUED"] = "QUEUED";
    EMessageAttemptState["ACCEPTED"] = "ACCEPTED";
    EMessageAttemptState["DELIVERED"] = "DELIVERED";
    EMessageAttemptState["FAILED"] = "FAILED";
    EMessageAttemptState["UNKNOWN"] = "UNKNOWN";
})(EMessageAttemptState || (EMessageAttemptState = {}));
export var MESSAGE_ATTEMPT_STATES = [
    EMessageAttemptState.QUEUED,
    EMessageAttemptState.ACCEPTED,
    EMessageAttemptState.DELIVERED,
    EMessageAttemptState.FAILED,
    EMessageAttemptState.UNKNOWN,
];
export var EProviderApproval;
(function (EProviderApproval) {
    EProviderApproval["PENDING"] = "PENDING";
    EProviderApproval["CONFIRMED"] = "CONFIRMED";
})(EProviderApproval || (EProviderApproval = {}));
export var EOutOfWindowPolicy;
(function (EOutOfWindowPolicy) {
    EOutOfWindowPolicy["DISABLED"] = "DISABLED";
    EOutOfWindowPolicy["UTILITY_MESSAGES"] = "UTILITY_MESSAGES";
})(EOutOfWindowPolicy || (EOutOfWindowPolicy = {}));
/**
 * How an account's readiness is established. Readiness always comes from the
 * provider's check; `ADMIN_CONFIRMED` is parse-only, read as `PROVIDER_CHECK`
 * and never written. Whether the check counts is the check's identity
 * (`checkIsFor`), never a stored policy.
 */
export var EReadinessPolicy;
(function (EReadinessPolicy) {
    EReadinessPolicy["PROVIDER_CHECK"] = "PROVIDER_CHECK";
    EReadinessPolicy["ADMIN_CONFIRMED"] = "ADMIN_CONFIRMED";
})(EReadinessPolicy || (EReadinessPolicy = {}));
/** Where an account's provider approval comes from (`AccountCheck::approval`). */
export var EApprovalSource;
(function (EApprovalSource) {
    /** The provider reports it, and the check read it. */
    EApprovalSource["PROVIDER_CHECK"] = "PROVIDER_CHECK";
    /** The provider has no API for it: an administrator entered it. */
    EApprovalSource["ADMIN_ENTERED"] = "ADMIN_ENTERED";
})(EApprovalSource || (EApprovalSource = {}));
export var EReadinessBlocker;
(function (EReadinessBlocker) {
    EReadinessBlocker["UNSUPPORTED_PROVIDER"] = "UNSUPPORTED_PROVIDER";
    /**
     * The account's last check was of another identity (an earlier number,
     * Page or sender): nothing it said counts until it is checked again.
     */
    EReadinessBlocker["UNCHECKED"] = "UNCHECKED";
    EReadinessBlocker["NOT_CONNECTED"] = "NOT_CONNECTED";
    EReadinessBlocker["UNSUPPORTED_PURPOSE"] = "UNSUPPORTED_PURPOSE";
    EReadinessBlocker["NEEDS_PROVIDER_APPROVAL"] = "NEEDS_PROVIDER_APPROVAL";
    EReadinessBlocker["NEEDS_PRODUCTION_ACCESS"] = "NEEDS_PRODUCTION_ACCESS";
    EReadinessBlocker["NEEDS_APPROVED_TEMPLATE"] = "NEEDS_APPROVED_TEMPLATE";
    /**
     * The webhook component's public address isn't configured: no callback
     * reaches the account. Messenger codes need Get Started, which arrives as
     * a callback; receipts are unavailable (`callback_readiness`).
     */
    EReadinessBlocker["CALLBACK_NOT_CONFIGURED"] = "CALLBACK_NOT_CONFIGURED";
    /**
     * A Viber account without report credentials: Infobip's subscription
     * can't authenticate its reports, so receipts are unavailable.
     */
    EReadinessBlocker["REPORT_CREDENTIALS_MISSING"] = "REPORT_CREDENTIALS_MISSING";
})(EReadinessBlocker || (EReadinessBlocker = {}));
export var ECredentialName;
(function (ECredentialName) {
    ECredentialName["ACCESS_TOKEN"] = "ACCESS_TOKEN";
    ECredentialName["APP_SECRET"] = "APP_SECRET";
    ECredentialName["VERIFY_TOKEN"] = "VERIFY_TOKEN";
    ECredentialName["API_KEY"] = "API_KEY";
    ECredentialName["SMTP_PASSWORD"] = "SMTP_PASSWORD";
    ECredentialName["AWS_ACCESS_KEY_ID"] = "AWS_ACCESS_KEY_ID";
    ECredentialName["AWS_SECRET_ACCESS_KEY"] = "AWS_SECRET_ACCESS_KEY";
    /** Basic auth Infobip's report subscription sends; generated, shown once. */
    ECredentialName["REPORT_USERNAME"] = "REPORT_USERNAME";
    ECredentialName["REPORT_PASSWORD"] = "REPORT_PASSWORD";
})(ECredentialName || (ECredentialName = {}));
/** Credentials Step generates and shows once; never typed. */
export var GENERATED_CREDENTIALS = [
    ECredentialName.VERIFY_TOKEN,
    ECredentialName.REPORT_USERNAME,
    ECredentialName.REPORT_PASSWORD,
];
/** Parse-only: a notification goes to each voter's own channel. Never written. */
export var EChannelSelection;
(function (EChannelSelection) {
    EChannelSelection["SINGLE_CHANNEL"] = "SINGLE_CHANNEL";
    EChannelSelection["VOTER_PREFERENCE"] = "VOTER_PREFERENCE";
})(EChannelSelection || (EChannelSelection = {}));
/**
 * Message keys Keycloak sends as `template_key`, offered when binding an
 * approved template to a specific message. Mirrors the `template_key` values
 * of the Keycloak messaging extensions: `otp` for a one-time code, `otl`
 * for a one-time link, and the message key of each notice.
 */
export var KEYCLOAK_OTP_MESSAGE_KEYS = ["otp", "otl"];
export var KEYCLOAK_NOTICE_MESSAGE_KEYS = [
    "messageSuccessSms",
    "messageSuccessSmsKiosk",
    "messagePendingSms",
    "messageRejectedSms",
    "forgotPassword.sms.text",
    "newPassword.message.text",
    "contactChanged.notice.text",
];
/** Keycloak user attributes of a voter's one channel (written after a verified code). */
export var VOTER_ATTR_MESSAGE_CHANNEL = "sequent.read-only.message-channel";
export var VOTER_ATTR_MESSAGE_NUMBER = "sequent.read-only.message-number";
export var VOTER_ATTR_MESSENGER_ID = "sequent.read-only.messenger-id";
export var VOTER_ATTR_MESSENGER_PAGE = "sequent.read-only.messenger-page";
export var VOTER_ATTR_MESSAGE_CONSENT = "sequent.read-only.message-consent";
export var VOTER_ATTR_MOBILE_NUMBER = "sequent.read-only.mobile-number";
/** Read mapping only: a record of the first messaging release. Never written. */
export var VOTER_ATTR_WHATSAPP_NUMBER = "sequent.read-only.whatsapp-number";
export var VOTER_ATTR_VIBER_NUMBER = "sequent.read-only.viber-number";
export var VOTER_ATTR_VERIFIED_CHANNELS = "sequent.read-only.verified-channels";
